import { NavLink, Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown, ChevronRight } from "lucide-react";

const nav = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  {
    to: "/services",
    label: "Services",
    children: [
      { to: "/services#ai-marketing-consulting", label: "AI Marketing Consulting" },
      { to: "/services#performance-marketing", label: "Performance Marketing" },
      { to: "/services#cro", label: "CRO" },
      { to: "/services#marketing-automation", label: "Marketing Automation" },
      { to: "/services#google-ads", label: "Google Ads" },
      { to: "/services#meta-ads", label: "Meta Ads" },
    ],
  },
  {
    to: "/work",
    label: "Case Studies",
    children: [
      { to: "/work#ai-agent", label: "AI Marketing Agent" },
      { to: "/work#ipl", label: "IPL Marketing" },
      { to: "/work#styx", label: "OTT Growth" },
      { to: "/work#above40", label: "D2C Growth" },
      { to: "/approach#cro", label: "CRO Projects" },
    ],
  },
  {
    to: "/insights",
    label: "Insights",
    children: [
      { to: "/insights#ai-marketing", label: "AI Marketing" },
      { to: "/insights#google-ads", label: "Google Ads" },
      { to: "/insights#cro", label: "CRO" },
      { to: "/insights#ga4", label: "GA4" },
      { to: "/insights#growth-strategy", label: "Growth Strategy" },
    ],
  },
  { to: "/contact", label: "Contact" },
];

const DesktopItem = ({ item, isDark }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const base = "text-sm font-medium tracking-tight transition inline-flex items-center gap-1";
  const inactive = isDark ? "text-white/60 hover:text-white" : "text-neutral-500 hover:text-neutral-900";
  const active = isDark ? "text-white" : "text-neutral-900";

  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        end={item.to === "/"}
        data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
        className={({ isActive }) => `${base} ${isActive ? active : inactive}`}
      >
        {item.label}
      </NavLink>
    );
  }

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <NavLink
        to={item.to}
        data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
        className={({ isActive }) => `${base} ${isActive ? active : inactive}`}
      >
        {item.label}
        <ChevronDown size={12} className="opacity-60" />
      </NavLink>
      {open && (
        <div
          data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/\s/g, "-")}`}
          className={`absolute left-0 top-full pt-3 w-64 z-50`}
        >
          <div className={`rounded-xl overflow-hidden border shadow-2xl ${isDark ? "bg-[#0F1115] border-white/10" : "bg-white border-neutral-200"}`}>
            <div className={`px-4 py-2 text-[10px] font-mono uppercase tracking-[0.2em] ${isDark ? "text-white/40" : "text-neutral-500"}`}>
              {item.label}
            </div>
            <ul className="pb-2">
              {item.children.map((c) => (
                <li key={c.to}>
                  <Link
                    to={c.to}
                    onClick={() => setOpen(false)}
                    className={`flex items-center justify-between px-4 py-2 text-sm ${isDark ? "text-white/80 hover:bg-white/[0.03]" : "text-neutral-700 hover:bg-neutral-50"}`}
                  >
                    <span>{c.label}</span>
                    <ChevronRight size={12} className="opacity-40" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

const MobileItem = ({ item, close, isDark }) => {
  const [open, setOpen] = useState(false);
  const linkCls = isDark ? "text-white/70" : "text-neutral-700";
  if (!item.children) {
    return (
      <Link
        to={item.to}
        onClick={close}
        className={`block py-2 text-sm ${linkCls}`}
      >
        {item.label}
      </Link>
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between py-2">
        <Link to={item.to} onClick={close} className={`text-sm ${linkCls}`}>{item.label}</Link>
        <button
          onClick={() => setOpen((v) => !v)}
          className={`p-1 rounded ${isDark ? "text-white/50" : "text-neutral-500"}`}
          aria-label={`Expand ${item.label}`}
        >
          <ChevronDown size={14} className={`transition ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <ul className={`pl-4 pb-2 space-y-1 border-l ${isDark ? "border-white/10" : "border-neutral-200"} ml-1`}>
          {item.children.map((c) => (
            <li key={c.to}>
              <Link to={c.to} onClick={close} className={`block py-1.5 text-xs ${isDark ? "text-white/50 hover:text-white" : "text-neutral-500 hover:text-neutral-900"}`}>
                {c.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const Navbar = ({ variant = "auto" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const isDark = variant === "dark";
  const textColor = isDark ? "text-white/90" : "text-neutral-900";
  const subText = isDark ? "text-white/50" : "text-neutral-500";
  const bg = scrolled
    ? isDark
      ? "bg-[#0F1115]/80 backdrop-blur-md border-b border-white/5"
      : "bg-white/85 backdrop-blur-md border-b border-neutral-200"
    : "bg-transparent";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${bg}`}
    >
      <nav className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 py-5 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="nav-logo"
          className={`font-display font-semibold text-lg tracking-tight ${textColor} shrink-0`}
        >
          <span className="inline-flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#3B82F6] pulse-dot" />
            Shivani<span className={subText}>Ranawat</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {nav.map((item) => (
            <DesktopItem key={item.to} item={item} isDark={isDark} />
          ))}
        </div>

        <Link
          to="/contact"
          data-testid="nav-cta-book"
          className="hidden lg:inline-flex items-center gap-2 rounded-full bg-[#3B82F6] text-white px-4 py-2 text-sm font-medium hover:bg-[#2563EB] transition shrink-0"
        >
          Book a Growth Audit
          <span className="text-white/80">→</span>
        </Link>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden ${textColor}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className={`lg:hidden border-t ${isDark ? "border-white/10 bg-[#0F1115]" : "border-neutral-200 bg-white"}`}
        >
          <div className="px-6 py-4 flex flex-col">
            {nav.map((item) => (
              <MobileItem key={item.to} item={item} close={() => setOpen(false)} isDark={isDark} />
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-3 inline-flex items-center justify-center rounded-full bg-[#3B82F6] text-white px-4 py-2 text-sm"
            >
              Book a Growth Audit →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
